// @ts-strict-ignore
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonWithLoading } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { Toggle } from '@actual-app/components/toggle';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { Error, Warning } from '#components/alerts';
import { Link } from '#components/common/Link';
import {
  Modal,
  ModalButtons,
  ModalCloseButton,
  ModalHeader,
} from '#components/common/Modal';
import { FormField, FormLabel } from '#components/forms';
import { useMultiuserEnabled } from '#components/ServerContext';
import { useAccounts } from '#hooks/useAccounts';
import { useCurrentAccess } from '#hooks/useCurrentAccess';
import type { Modal as ModalType } from '#modals/modalsSlice';
import { getSecretsError } from '#util/error';

type SimpleFinInitialiseModalProps = Extract<
  ModalType,
  { name: 'simplefin-init' }
>['options'];

export const SimpleFinInitialiseModal = ({
  onSuccess,
  credentialSource,
}: SimpleFinInitialiseModalProps) => {
  const { t } = useTranslation();
  const { cloudFileId, isAdmin: canSetGlobalCredentials } = useCurrentAccess();
  const multiuserEnabled = useMultiuserEnabled();
  const { data: accounts = [] } = useAccounts();
  const linkedAccountCount = accounts.filter(
    account => account.account_sync_source === 'simpleFin' && !account.closed,
  ).length;
  const [token, setToken] = useState('');
  // Keep editing an existing token where it is stored. A new token defaults to
  // this budget on multi-user servers so it isn't shared with every user.
  const [perBudgetFile, setPerBudgetFile] = useState(
    !canSetGlobalCredentials ||
      (credentialSource
        ? credentialSource === 'per-budget-file'
        : multiuserEnabled),
  );
  const [isValid, setIsValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(t('It is required to provide a token.'));

  const onSubmit = async (close: () => void) => {
    if (!token) {
      setIsValid(false);
      return;
    }

    setIsLoading(true);

    const fileId = perBudgetFile ? cloudFileId : null;
    if (perBudgetFile && !fileId) {
      setIsLoading(false);
      setIsValid(false);
      setError(t('Budget file ID is required.'));
      return;
    }

    const { error, reason } =
      (await send('secret-set', {
        name: 'simplefin_token',
        value: token,
        fileId,
      })) || {};

    if (error) {
      setIsValid(false);
      setError(getSecretsError(error, reason));
    } else {
      await send('secret-set', {
        name: 'simplefin_accessKey',
        value: null,
        fileId,
      });
      onSuccess(perBudgetFile);
    }
    setIsLoading(false);
    close();
  };

  return (
    <Modal name="simplefin-init" containerProps={{ style: { width: 300 } }}>
      {({ state }) => (
        <>
          <ModalHeader
            title={t('Set-up SimpleFIN')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />
          <View style={{ display: 'flex', gap: 10 }}>
            <Text>
              <Trans>
                In order to enable bank sync via SimpleFIN (only for North
                American banks), you will need to create a token. This can be
                done by creating an account with{' '}
                <Link
                  variant="external"
                  to="https://bridge.simplefin.org/"
                  linkColor="purple"
                >
                  SimpleFIN
                </Link>
                .
              </Trans>
            </Text>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <Text>
                <Trans>For this budget only</Trans>
              </Text>
              <Toggle
                id="simplefin-per-budget-file"
                isOn={perBudgetFile}
                isDisabled={!canSetGlobalCredentials}
                onToggle={setPerBudgetFile}
              />
            </View>

            {linkedAccountCount > 0 && (
              <Warning>
                <Trans count={linkedAccountCount}>
                  {{ count: linkedAccountCount }} accounts in this budget are
                  linked to SimpleFIN. If this token is for a different
                  SimpleFIN account, you will need to unlink and relink them.
                </Trans>
              </Warning>
            )}

            <FormField>
              <FormLabel title={t('Token:')} htmlFor="token-field" />
              <Input
                id="token-field"
                type="password"
                value={token}
                onChangeValue={value => {
                  setToken(value);
                  setIsValid(true);
                }}
              />
            </FormField>

            {!isValid && <Error>{error}</Error>}
          </View>

          <ModalButtons>
            <ButtonWithLoading
              variant="primary"
              autoFocus
              isLoading={isLoading}
              onPress={() => {
                void onSubmit(() => state.close());
              }}
            >
              <Trans>Save and continue</Trans>
            </ButtonWithLoading>
          </ModalButtons>
        </>
      )}
    </Modal>
  );
};
